import ChevronLeft from "lucide-react/dist/esm/icons/chevron-left";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import ChevronsLeft from "lucide-react/dist/esm/icons/chevrons-left";
import ChevronsRight from "lucide-react/dist/esm/icons/chevrons-right";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface BrowsePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Total matching results, when known — shown next to the page summary. */
  totalItems?: number;
  /** Current page size; rendering the selector requires onPageSizeChange too. */
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: readonly number[];
}

export function BrowsePagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
}: BrowsePaginationProps) {
  const showPageSizePicker =
    pageSize !== undefined &&
    onPageSizeChange !== undefined &&
    // Pointless when everything already fits on one page of the smallest size.
    (totalItems === undefined || totalItems > Math.min(...pageSizeOptions));

  if (totalPages <= 1 && !showPageSizePicker) return null;

  const getVisiblePages = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("ellipsis");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="flex flex-col items-center gap-3 pt-8 pb-4">
      <nav aria-label="Pagination" className="flex items-center justify-center gap-1.5">
        <Button
          variant="outline"
          size="icon"
          className="hidden sm:inline-flex h-9 w-9 border-border/50"
          disabled={currentPage === 1}
          onClick={() => onPageChange(1)}
          aria-label="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 border-border/50"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {getVisiblePages().map((page, i) =>
          page === "ellipsis" ? (
            <span key={`e-${i}`} className="px-1 text-muted-foreground select-none">…</span>
          ) : (
            <Button
              key={page}
              variant={page === currentPage ? "default" : "outline"}
              size="icon"
              className={`h-9 w-9 text-sm font-medium ${
                page === currentPage
                  ? "text-primary-foreground"
                  : "border-border/50 hover:border-primary/40"
              }`}
              onClick={() => onPageChange(page)}
              aria-label={page === totalPages ? `Last page, page ${page}` : `Page ${page}`}
              aria-current={page === currentPage ? "page" : undefined}
            >
              {page}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 border-border/50"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="hidden sm:inline-flex h-9 w-9 border-border/50"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(totalPages)}
          aria-label={`Last page, page ${totalPages}`}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </nav>

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="font-mono">
          Page {currentPage} of {totalPages}
          {totalItems !== undefined && ` · ${totalItems} asset${totalItems === 1 ? "" : "s"}`}
        </span>
        {showPageSizePicker && (
          <label className="flex items-center gap-2">
            <span>Per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
              <SelectTrigger className="h-8 w-[72px] border-border/50 text-xs" aria-label="Results per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        )}
      </div>
    </div>
  );
}
